import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from 'ff/debrief/store';
import { tracklogApi as api } from 'ff/debrief/features/TracklogApi';
import { DebriefStateResponse } from 'ff/debrief/models';

export interface StatusState {
    tracks: {
        [trackId: string]: {
            isReanalyzing: boolean;
            pollStartTime?: number;
        };
    };
}

const initialStatusState: StatusState = {
    tracks: {},
};

export const statusSlice = createSlice({
    name: 'StatusSlice',
    initialState: initialStatusState,
    reducers: {
        pollingOff: (state, action: PayloadAction<string>) => {
            state.tracks[action.payload] = {
                isReanalyzing: false,
            };
        },
        pollingOn: (state, action: PayloadAction<string>) => {
            state.tracks[action.payload] = {
                isReanalyzing: true,
                pollStartTime: Date.now(),
            };
        },
    },
});

const isReanalysisComplete = (
    newDebriefState?: DebriefStateResponse,
    initialDebriefState?: DebriefStateResponse
) => {
    if (
        newDebriefState?.lastUpdatedUtc <= initialDebriefState?.lastUpdatedUtc
    ) {
        return false;
    }

    return (
        newDebriefState.status === 'COMPLETED' ||
        newDebriefState.status === 'SKIPPED' ||
        newDebriefState.status === 'SUCCESS' ||
        newDebriefState.status === 'ERROR'
    );
};

const hasReanalysisTimeBeenExceeded = (startTime: number) => {
    const currentTime = Date.now();
    const elapsedTime = currentTime - startTime;
    return elapsedTime > 30000;
};

export function pollForReanalysisComplete(trackId: string): AppThunk {
    return async (dispatch, getState) => {
        const {
            endpoints: { getDebriefState, getDebriefByTrackUUID },
            util: { invalidateTags },
        } = api;

        const initialState = getDebriefState.select(trackId)(getState());

        // Start polling the status endpoint
        dispatch(actions.pollingOn(trackId));
        const query = dispatch(
            getDebriefState.initiate(trackId, {
                subscriptionOptions: { pollingInterval: 2000 },
            })
        );

        const interval = setInterval(() => {
            const currentState = getDebriefState.select(trackId)(getState());
            const reanalysisTimeExceeded = hasReanalysisTimeBeenExceeded(
                getState().statusSlice.tracks[trackId].pollStartTime
            );
            const reanalysisComplete = isReanalysisComplete(
                currentState.data,
                initialState.data
            );

            // If reanalysis is done, or we've exceeded the amount of time we want to poll, stop polling
            if (reanalysisComplete || reanalysisTimeExceeded) {
                query.unsubscribe();
                clearInterval(interval);
                dispatch(actions.pollingOff(trackId));
            }

            if (reanalysisComplete) {
                try {
                    dispatch(
                        invalidateTags([
                            { type: 'Debrief', id: trackId },
                            'Tracklogs',
                        ])
                    );
                    dispatch(getDebriefByTrackUUID.initiate(trackId));
                } catch (e) {
                    console.error('Error reloading analysis:', e);
                }
            }
        }, 1000);
    };
}

export const actions = statusSlice.actions;
